import React, { useState, useEffect } from 'react';
import { ApiClient, API_BASE } from '../../api/client';
import { DEPLOYMENT } from '../../config/deployment';
import { useAuth } from '../../context/AuthContext';
import { DatabaseResetWizardModal } from './DatabaseResetWizardModal';
import { announceMaintenance } from './MaintenanceOverlay';
import {
  Database, Download, Upload, ShieldCheck, AlertTriangle,
  RotateCcw, CheckCircle2, Wrench, FileText,
  Lock, Unlock, RefreshCw, Server, ExternalLink
} from 'lucide-react';

export const BackupRestoreView: React.FC = () => {
  const { user } = useAuth();
  const [scope, setScope] = useState<'prod' | 'demo' | 'full'>('full');
  const [isExporting, setIsExporting] = useState(false);
  const [isResetWizardOpen, setIsResetWizardOpen] = useState(false);

  // Restore state
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreFeedback, setRestoreFeedback] = useState<{ ok: boolean; message: string; statements?: number } | null>(null);

  // Maintenance state
  const [maintenance, setMaintenance] = useState<{ active: boolean; reason: string; started_at: string | null }>({
    active: false,
    reason: '',
    started_at: null
  });
  const [customReason, setCustomReason] = useState('');
  const [isUpdatingMaintenance, setIsUpdatingMaintenance] = useState(false);

  // Mirror State (külső PHP/MySQL tárhely – S14: a cím a konfigurációból jön)
  const [mirrorStatus, setMirrorStatus] = useState<{
    mirror_url: string;
    last_sync_at: string | null;
    last_sync_status: string;
    configured: boolean;
  } | null>(null);
  const [isTriggeringMirror, setIsTriggeringMirror] = useState(false);
  const [mirrorResult, setMirrorResult] = useState<{ ok: boolean; message: string; duration_ms?: number } | null>(null);

  const fetchMirrorStatus = async () => {
    try {
      const res = await ApiClient.getMirrorStatus();
      if (res.ok && res.data) {
        setMirrorStatus(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleTriggerMirror = async () => {
    setIsTriggeringMirror(true);
    setMirrorResult(null);
    try {
      const res = await ApiClient.triggerMirrorSync();
      if (res.ok && res.data) {
        setMirrorResult({
          ok: true,
          message: res.data.message || 'Szinkronizáció sikeresen lefutott!',
          duration_ms: res.data.duration_ms,
        });
        fetchMirrorStatus();
      } else {
        setMirrorResult({
          ok: false,
          message: res.error || 'A szinkronizáció sikertelen volt.',
        });
      }
    } catch (err: any) {
      setMirrorResult({
        ok: false,
        message: err.message || 'Hiba történt a szinkronizáció indításakor.',
      });
    } finally {
      setIsTriggeringMirror(false);
    }
  };

  const fetchMaintenance = async () => {
    try {
      const res = await ApiClient.request<{ active: boolean; reason: string; started_at: string | null }>('/system/maintenance');
      if (res && typeof res.active === 'boolean') {
        setMaintenance({
          active: Boolean(res.active),
          reason: res.reason || '',
          started_at: res.started_at || null
        });
        setCustomReason(res.reason || '');
        if ((res as any).check_interval_sec) setCheckInterval(Number((res as any).check_interval_sec));
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchMaintenance();
    fetchMirrorStatus();
  }, []);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const token = ApiClient.getToken();
      const res = await fetch(`${API_BASE}/admin/backup/export?scope=${scope}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) {
        throw new Error(`Szerver válasz: ${res.status} ${res.statusText}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kozad_backup_${scope}_${new Date().toISOString().slice(0, 10)}.sql`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert('Hiba a mentés letöltése során: ' + (err.message || 'Ismeretlen hiba'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleRestore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!restoreFile) {
      alert('Kérjük, válasszon ki egy .sql mentési fájlt!');
      return;
    }
    if (confirmText.trim().toUpperCase() !== 'VISSZAÁLLÍTÁS') {
      alert('A megerősítéshez írja be pontosan: VISSZAÁLLÍTÁS');
      return;
    }

    if (!window.confirm('FIGYELEM: Az adatbázis visszaállítása felülírja a mentésben szereplő adatokat! A folyamat idejére a Karbantartási Mód automatikusan bekapcsol. Biztosan folytatja?')) {
      return;
    }

    setIsRestoring(true);
    setRestoreFeedback(null);

    try {
      const formData = new FormData();
      formData.append('file', restoreFile);

      const token = ApiClient.getToken();
      const res = await fetch(`${API_BASE}/admin/backup/restore`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: formData
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'A visszaállítás sikertelen');
      }

      setRestoreFeedback({
        ok: true,
        message: data.message || 'Sikeres visszaállítás!',
        statements: data.executedStatements
      });
      setRestoreFile(null);
      setConfirmText('');
      await fetchMaintenance();
    } catch (err: any) {
      setRestoreFeedback({
        ok: false,
        message: err.message || 'Ismeretlen hiba történt a visszaállítás során'
      });
    } finally {
      setIsRestoring(false);
    }
  };

  // Automatikus ellenőrzés időköze (mp): a felhasználók felülete ilyen gyakran nézi meg a karbantartás állapotát
  const [checkInterval, setCheckInterval] = useState(60);
  const [intervalSaved, setIntervalSaved] = useState(false);
  const saveCheckInterval = async () => {
    const v = Math.min(900, Math.max(10, Math.round(Number(checkInterval) || 60)));
    const res: any = await ApiClient.request('/system/maintenance', { method: 'POST', body: JSON.stringify({ check_interval_sec: v }) });
    if (res?.ok) {
      setCheckInterval(res.check_interval_sec);
      announceMaintenance({ active: res.active, reason: res.reason, started_at: res.started_at || null, check_interval_sec: res.check_interval_sec });
      setIntervalSaved(true);
      setTimeout(() => setIntervalSaved(false), 2500);
    } else {
      alert(res?.error || 'Az időköz mentése nem sikerült');
    }
  };

  const handleToggleMaintenance = async (enable: boolean) => {
    setIsUpdatingMaintenance(true);
    try {
      const res = await ApiClient.request<{ ok: boolean; active: boolean; message: string; reason: string }>('/system/maintenance', {
        method: 'POST',
        body: JSON.stringify({
          active: enable,
          reason: customReason || (enable ? 'Tervezett rendszerkarbantartás és adatbázis művelet' : '')
        })
      });
      if (res && res.ok) {
        setMaintenance(prev => ({
          ...prev,
          active: res.active,
          reason: res.reason
        }));
        // a sáv / zároló ablak azonnal frissüljön (oldalfrissítés nélkül)
        announceMaintenance({ active: res.active, reason: res.reason, started_at: (res as any).started_at || null, check_interval_sec: (res as any).check_interval_sec });
      }
    } catch (err: any) {
      alert('Hiba a karbantartási mód módosításakor: ' + (err.message || 'Ismeretlen hiba'));
    } finally {
      setIsUpdatingMaintenance(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-4 sm:p-6">
      {/* Fejléc */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-200 dark:border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Database className="w-7 h-7 text-indigo-600 dark:text-cyan-400" />
            <span>Adatbázis Mentés & Visszaállítás Központ</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Zero-downtime biztonsági mentések letöltése, tiszta Éles és DEMO szétválasztás, valamint karbantartási zárolt visszaállítás.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchMaintenance}
            className="p-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 transition cursor-pointer shadow-xs"
            title="Állapot frissítése"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 3 Oszlopos / Grid elrendezés a funkcióknak */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* 1. KÁRTYA: MENTÉS KÉSZÍTÉSE (Zero-Downtime) */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2.5 text-indigo-600 dark:text-cyan-400">
              <Download className="w-6 h-6" />
              <h2 className="font-bold text-lg text-slate-900 dark:text-white">
                1. Biztonsági Mentés Készítése
              </h2>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Válassza ki a mentés típusát. A fájl azonnal letölthető szabványos SQLite / D1 SQL scriptként (.sql), amely tartalmazza az összes tábladefiníciót és adatot.
            </p>

            {/* Scope Választó Kártyák */}
            <div className="space-y-2.5">
              <label
                onClick={() => setScope('prod')}
                className={`flex items-start gap-3 p-3.5 rounded-xl border transition cursor-pointer ${
                  scope === 'prod'
                    ? 'border-indigo-500 bg-indigo-50/70 dark:bg-indigo-950/30 text-indigo-950 dark:text-indigo-200 ring-2 ring-indigo-500/20'
                    : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'prod'}
                  onChange={() => setScope('prod')}
                  className="mt-1 text-indigo-600 focus:ring-indigo-500"
                />
                <div>
                  <div className="font-bold text-sm flex items-center gap-1.5">
                    <span>🟢 Csak Éles Adatok (PROD)</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300 font-semibold">
                      Ajánlott
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Csak a valós hivatalok, intézmények és hivatalos közzétételek. 100%-ban mentes minden teszt- és demo bejegyzéstől.
                  </p>
                </div>
              </label>

              <label
                onClick={() => setScope('demo')}
                className={`flex items-start gap-3 p-3.5 rounded-xl border transition cursor-pointer ${
                  scope === 'demo'
                    ? 'border-amber-500 bg-amber-50/70 dark:bg-amber-950/30 text-amber-950 dark:text-amber-200 ring-2 ring-amber-500/20'
                    : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'demo'}
                  onChange={() => setScope('demo')}
                  className="mt-1 text-amber-600 focus:ring-amber-500"
                />
                <div>
                  <div className="font-bold text-sm">🟡 Csak DEMO / Homokozó Adatok</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Kizárólag a minta-önkormányzat, a tesztintézmények és a homokozóban létrehozott teszt bejegyzések.
                  </p>
                </div>
              </label>

              <label
                onClick={() => setScope('full')}
                className={`flex items-start gap-3 p-3.5 rounded-xl border transition cursor-pointer ${
                  scope === 'full'
                    ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/30 text-blue-950 dark:text-blue-200 ring-2 ring-blue-500/20'
                    : 'border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-slate-700 dark:text-slate-300'
                }`}
              >
                <input
                  type="radio"
                  name="scope"
                  checked={scope === 'full'}
                  onChange={() => setScope('full')}
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-bold text-sm">🔵 Teljes Rendszermentés (FULL)</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Az adatbázis minden egyes táblája és sora (éles és demo egyaránt) teljes biztonsági tükörként.
                  </p>
                </div>
              </label>
            </div>

            {/* Zero-Downtime Tájékoztató Doboz */}
            <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 flex items-start gap-2.5 text-xs text-emerald-800 dark:text-emerald-300">
              <ShieldCheck className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" />
              <div>
                <span className="font-bold">Zero-Downtime (0 kiesés):</span> A mentés készítése közben a felhasználókat senki nem zárja ki, az éles munka és böngészés 100%-ban folyamatos és zavartalan marad.
              </div>
            </div>
          </div>

          <button
            onClick={handleExport}
            disabled={isExporting}
            className="w-full py-3 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm shadow-md flex items-center justify-center gap-2 transition cursor-pointer disabled:opacity-50"
          >
            <Download className={`w-4 h-4 ${isExporting ? 'animate-bounce' : ''}`} />
            <span>{isExporting ? 'Mentés generálása folyamatban...' : `Biztonsági Mentés Letöltése (${scope.toUpperCase()} .SQL)`}</span>
          </button>
        </div>

        {/* 2. KÁRTYA: VISSZAÁLLÍTÁS (Restore) */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs flex flex-col justify-between space-y-6">
          <form onSubmit={handleRestore} className="space-y-4">
            <div className="flex items-center gap-2.5 text-rose-600 dark:text-rose-400">
              <Upload className="w-6 h-6" />
              <h2 className="font-bold text-lg text-slate-900 dark:text-white">
                2. Adatbázis Visszaállítása (Restore)
              </h2>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Egy korábban letöltött .sql biztonsági mentés visszatöltése. A visszatöltés ideje alatt a rendszer automatikusan <strong className="text-amber-600">Karbantartási Módba</strong> lép a hibák elkerülése érdekében.
            </p>

            {/* Fájl feltöltő doboz */}
            <div className="p-4 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl text-center space-y-2 hover:border-indigo-400 transition bg-slate-50/50 dark:bg-slate-800/30">
              <FileText className="w-8 h-8 mx-auto text-slate-400" />
              <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                {restoreFile ? (
                  <span className="text-indigo-600 dark:text-cyan-400 font-bold">{restoreFile.name} ({(restoreFile.size / 1024).toFixed(1)} KB)</span>
                ) : (
                  <span>Kattintson az .SQL fájl kiválasztásához</span>
                )}
              </div>
              <input
                type="file"
                accept=".sql"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setRestoreFile(e.target.files[0]);
                  }
                }}
                className="hidden"
                id="restore-file-input"
              />
              <label
                htmlFor="restore-file-input"
                className="inline-block px-3 py-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-xs font-medium cursor-pointer hover:bg-slate-100 transition shadow-xs"
              >
                Fájl tallózása...
              </label>
            </div>

            {/* Figyelmeztetés és megerősítés */}
            <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/60 text-xs text-rose-800 dark:text-rose-300 space-y-2">
              <div className="flex items-center gap-1.5 font-bold">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>FIGYELEM: Visszaállítási Felülírás</span>
              </div>
              <p className="text-[11px] leading-relaxed">
                A visszaállítás a mentésben található állapotra állítja vissza az érintett rekordokat. A megerősítéshez írja be a lenti mezőbe: <strong>VISSZAÁLLÍTÁS</strong>
              </p>
              <input
                type="text"
                placeholder="Írja be: VISSZAÁLLÍTÁS"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg border border-rose-300 dark:border-rose-700 bg-white dark:bg-slate-900 font-mono font-bold"
              />
            </div>

            {restoreFeedback && (
              <div className={`p-3 rounded-xl text-xs flex items-center gap-2 font-semibold ${
                restoreFeedback.ok
                  ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-200 border border-emerald-300'
                  : 'bg-rose-100 dark:bg-rose-950 text-rose-800 dark:text-rose-200 border border-rose-300'
              }`}>
                {restoreFeedback.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />}
                <span>{restoreFeedback.message}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isRestoring || !restoreFile || confirmText.trim().toUpperCase() !== 'VISSZAÁLLÍTÁS'}
              className="w-full py-3 px-4 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold text-sm shadow-md flex items-center justify-center gap-2 transition cursor-pointer disabled:opacity-40"
            >
              <RotateCcw className={`w-4 h-4 ${isRestoring ? 'animate-spin' : ''}`} />
              <span>{isRestoring ? 'Adatbázis visszaállítása folyamatban...' : 'Adatbázis Visszaállításának Indítása'}</span>
            </button>
          </form>
        </div>
      </div>

      {/* 3. SZEKCIÓ: KARBANTARTÁSI MÓD KÉZI VEZÉRLÉSE */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <Wrench className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <h2 className="font-bold text-base text-slate-900 dark:text-white">
              Karbantartási Mód (Maintenance Mode) Kézi Vezérlése
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${
              maintenance.active
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-300'
                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300'
            }`}>
              {maintenance.active ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
              <span>{maintenance.active ? 'Karbantartás AKTÍV' : 'Rendszer NORMÁL üzemmódban'}</span>
            </span>
          </div>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400">
          Ha nagyobb műveletet vagy manuális migrálást végez, itt egyetlen kattintással zárolhatja a rendszert a látogatók és normál ügyintézők elől. Szuperadminként Ön a karbantartás alatt is zavartalanul hozzáfér minden adathoz.
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-3">
          <input
            type="text"
            placeholder="Karbantartási indoklás (pl. Tervezett adatbázis-optimalizálás 14:00 - 14:15 között)..."
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            className="flex-1 px-3.5 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
          />

          {maintenance.active ? (
            <button
              onClick={() => handleToggleMaintenance(false)}
              disabled={isUpdatingMaintenance}
              className="w-full sm:w-auto px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition cursor-pointer shadow-xs flex items-center justify-center gap-1.5"
            >
              <Unlock className="w-3.5 h-3.5" />
              <span>Karbantartás Feloldása</span>
            </button>
          ) : (
            <button
              onClick={() => handleToggleMaintenance(true)}
              disabled={isUpdatingMaintenance}
              className="w-full sm:w-auto px-5 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold transition cursor-pointer shadow-xs flex items-center justify-center gap-1.5"
            >
              <Lock className="w-3.5 h-3.5" />
              <span>Karbantartási Mód Aktiválása</span>
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300 pt-1">
          <label htmlFor="maint-interval" className="font-medium">Automatikus ellenőrzés a felhasználóknál:</label>
          <input
            id="maint-interval"
            type="number"
            min={10}
            max={900}
            step={5}
            value={checkInterval}
            onChange={(e) => setCheckInterval(Number(e.target.value))}
            className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
          />
          <span>másodpercenként (10–900)</span>
          <button
            type="button"
            onClick={saveCheckInterval}
            className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold cursor-pointer"
          >
            Mentés
          </button>
          {intervalSaved && <span className="text-emerald-600 dark:text-emerald-400 font-semibold">✓ Mentve</span>}
          <span className="basis-full text-[11px] text-slate-400">
            Ennyi időnként nézi meg magától minden megnyitott felület, hogy tart-e még a karbantartás; a felhasználó a zároló ablakon kézzel is ellenőrizhet.
          </span>
        </div>
      </div>

      {/* 4. SZEKCIÓ: KÜLSŐ WEBTÁRHELY TÜKÖR & KATASZTRÓFA-HELYREÁLLÍTÁS */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <Server className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            <div>
              <h2 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2">
                <span>Külső Webtárhely Tükör & Katasztrófa-védelem</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 font-semibold border border-emerald-300/40">
                  {DEPLOYMENT.mirrorLabel} (PHP 8.4 + MySQL + 30GB)
                </span>
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Független, fizikai offline másolat és vészhelyzeti kiszolgáló a Cloudflare-en kívüli infrastruktúrán.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchMirrorStatus}
              className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs transition cursor-pointer"
              title="Tükör állapot frissítése"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            {DEPLOYMENT.mirrorDashboardUrl && <a
              href={DEPLOYMENT.mirrorDashboardUrl}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer"
            >
              <span>PHP Műszerfal</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>}
          </div>
        </div>

        {/* Állapot és KPI Kártyák */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 space-y-1">
            <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">Tükör Státusz</div>
            <div className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-full ${mirrorStatus?.last_sync_status === 'success' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
              <span>{mirrorStatus?.last_sync_status === 'success' ? 'SZINKRONIZÁLVA' : 'Készenlétben'}</span>
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 space-y-1">
            <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">Utolsó Mentés Időpontja</div>
            <div className="font-bold text-sm text-slate-900 dark:text-white">
              {mirrorStatus?.last_sync_at ? new Date(mirrorStatus.last_sync_at).toLocaleString('hu-HU') : 'Még nem futott kézi szinkronizáció'}
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 space-y-1">
            <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">Dedikált Tárhely</div>
            <div className="font-bold text-sm text-slate-900 dark:text-white">
              30 GB Merevlemez (PDF & BLOB)
            </div>
          </div>
        </div>

        {/* Tájékoztató Doboz */}
        <div className="p-3.5 rounded-xl bg-blue-50/70 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/50 text-xs text-blue-900 dark:text-blue-200 leading-relaxed space-y-1">
          <div className="font-bold flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span>Katasztrófa-helyreállítási védelem (Cloudflare kiesés esetére):</span>
          </div>
          <p className="text-[11px] text-blue-800 dark:text-blue-300">
            Ez a folyamat a Cloudflare D1 relációs adatbázist és az R2 objektumtárolóban lévő dokumentumokat párhuzamosan tükrözi a <code>{DEPLOYMENT.mirrorLabel}</code> MySQL szerverére és a helyi 30 GB-os háttértárra. Ha a Cloudflare bármikor elérhetetlenné válna, az összes adat és csatolt határozat/rendelet azonnal rendelkezésre áll a saját szervereden.
          </p>
        </div>

        {/* Eredmény üzenet */}
        {mirrorResult && (
          <div className={`p-3 rounded-xl border text-xs font-medium flex items-center gap-2 ${
            mirrorResult.ok
              ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
              : 'bg-rose-50 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-200'
          }`}>
            <span>{mirrorResult.ok ? '✅' : '❌'}</span>
            <span>{mirrorResult.message}</span>
            {mirrorResult.duration_ms && (
              <span className="text-[11px] opacity-75">({(mirrorResult.duration_ms / 1000).toFixed(1)} mp alatt)</span>
            )}
          </div>
        )}

        {/* Akció Gomb */}
        <div className="pt-2">
          <button
            onClick={handleTriggerMirror}
            disabled={isTriggeringMirror}
            className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-md transition cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isTriggeringMirror ? 'animate-spin' : ''}`} />
            <span>{isTriggeringMirror ? 'Szinkronizáció folyamatban a távoli szerveren...' : `⚡ Azonnali Külső Tükrözés Indítása (${DEPLOYMENT.mirrorLabel})`}</span>
          </button>
        </div>
      </div>

      {/* 5. SZEKCIÓ: SZUPERADMIN ADATBÁZIS KINULLÁZÁS ÉS GYÁRI ÚJRABETÖLTÉS */}
      {user?.role === 'superadmin' && (
        <div className="bg-gradient-to-br from-rose-50/90 via-white to-amber-50/80 dark:from-rose-950/20 dark:via-slate-900 dark:to-amber-950/20 border-2 border-rose-200 dark:border-rose-900/60 rounded-2xl p-6 shadow-xs space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <div className="p-3 rounded-2xl bg-rose-600 text-white shadow-md shadow-rose-600/20 shrink-0 mt-0.5">
                <RotateCcw className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-bold text-base text-rose-950 dark:text-rose-100">
                    Adatbázis Kinullázása & Gyári Újrabetöltése
                  </h2>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-200 dark:bg-rose-900 text-rose-800 dark:text-rose-200 font-extrabold uppercase tracking-wide">
                    SZUPERADMIN
                  </span>
                </div>
                <p className="text-xs text-rose-800/90 dark:text-rose-300/90 mt-1 max-w-2xl leading-relaxed">
                  Rendszertisztítás és újrainicializálás: automatikus biztonsági mentés felajánlással, a szuperadmin felhasználók védelmével, a 2026-os hivatalos szervezetek, a jogszabálytár és a 101 mappasablon pont egyedi adatokkal történő automatikus felépítésével.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsResetWizardOpen(true)}
              className="px-6 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs shadow-md shadow-rose-600/20 flex items-center justify-center gap-2 transition cursor-pointer shrink-0"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Adatbázis Kinullázása & Építése...</span>
            </button>
          </div>
        </div>
      )}

      {/* Database Reset Wizard Modal */}
      <DatabaseResetWizardModal
        isOpen={isResetWizardOpen}
        onClose={() => setIsResetWizardOpen(false)}
        onResetComplete={() => {
          fetchMaintenance();
          fetchMirrorStatus();
        }}
      />
    </div>
  );
};
